import { useCallback, useEffect, useState } from 'react'
import { Button, useConnectionRetry } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { useAuth } from '@pilot-hub/auth'
import { UserCheck } from 'lucide-react'
import { AufforderungsGruppe } from './AufforderungsGruppe'

// Meldung "Pilot zuordnen": von DJI Pilot 2 erkannte Flüge ohne Pilot (kein passendes "Flug starten"
// derselben Drohne in den 15 Minuten vor dem Abheben). Sichtbar für alle Einsatzkräfte im Pilot-Modul:
// Pilot/Co-Pilot ordnen sich selbst zu, jede Einsatzkraft kann einen Piloten des Einsatzes zuweisen.

interface OffenerFlug {
  id: number
  flug_nr: string | null
  startzeit: string | null
  laeuft: boolean
  drohne: string
  co_pilot_id: number | null
}

interface Kandidat { id: number; name: string; funktion_im_einsatz: string | null }

function FlugMeldung({ flug, istPilot, istCoPilot, onErledigt }: {
  flug: OffenerFlug; istPilot: boolean; istCoPilot: boolean; onErledigt: () => void
}) {
  const [kandidaten, setKandidaten] = useState<Kandidat[]>([])
  const [auswahl, setAuswahl] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    api.get(`/dji/fluege/${flug.id}/pilot-kandidaten`)
      .then(r => setKandidaten((r.data as { kandidaten: Kandidat[] }).kandidaten))
      .catch(() => {
        setKandidaten([])
        setFehler('Pilot-Vorschläge konnten nicht geladen werden – die Zuordnung ist trotzdem möglich.')
      })
  }, [flug.id])

  async function zuordnen(rolle: 'PILOT' | 'CO_PILOT', nutzerId?: number) {
    setLaeuft(true); setFehler(null)
    try {
      await api.post(`/fluege/${flug.id}/zuordnen`, nutzerId ? { rolle, nutzer_id: nutzerId } : { rolle })
      onErledigt()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Zuordnung fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  const [offen, setOffen] = useState(false)
  const uhr = flug.startzeit ? new Date(flug.startzeit).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '?'
  const mehr = kandidaten.length > 0
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1 basis-40 text-sm text-amber-100">
          <div className="truncate"><span className="font-semibold">{flug.drohne}</span> · {uhr} Uhr</div>
          <div className="truncate text-xs text-amber-200/80">{flug.laeuft ? 'gestartet' : 'geflogen'}{flug.flug_nr ? ` · ${flug.flug_nr}` : ''} – Pilot zuordnen</div>
        </div>
        {istPilot && <Button size="sm" className="!min-h-[48px] px-4" disabled={laeuft} onClick={() => zuordnen('PILOT')}>Ich bin Pilot</Button>}
        {istCoPilot && !flug.co_pilot_id && <Button size="sm" variant="secondary" className="!min-h-[48px] px-4" disabled={laeuft} onClick={() => zuordnen('CO_PILOT')}>Ich bin Co-Pilot</Button>}
        {mehr && (
          <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen}
            className="min-h-[48px] rounded-lg px-3 text-sm text-amber-100 hover:bg-amber-500/20">
            {offen ? 'Weniger ▲' : 'Anderer Pilot ▼'}
          </button>
        )}
      </div>
      {mehr && offen && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select value={auswahl} onChange={e => setAuswahl(e.target.value)} disabled={laeuft}
            className="min-h-[48px] min-w-0 flex-1 rounded-lg border border-amber-500/40 bg-dark-bg-tertiary px-3 text-sm text-dark-text">
            <option value="">— Pilot des Einsatzes wählen —</option>
            {kandidaten.map(k => (
              <option key={k.id} value={k.id}>{k.name}{k.funktion_im_einsatz === 'PILOT' ? ' (im Einsatz als Pilot)' : ''}</option>
            ))}
          </select>
          <Button size="sm" variant="secondary" className="min-h-[48px] px-4" disabled={!auswahl || laeuft} onClick={() => zuordnen('PILOT', Number(auswahl))}>Als Pilot zuweisen</Button>
        </div>
      )}
      {fehler && <div className="mt-2 text-sm text-red-200">{fehler}</div>}
    </div>
  )
}

export function DjiZuordnung() {
  const { user } = useAuth()
  const [fluege, setFluege] = useState<OffenerFlug[]>([])
  const funktionen: string[] = (user as any)?.funktionen || []

  const laden = useCallback(() => {
    if (document.visibilityState === 'hidden') return
    // Fehler: letzte Liste behalten (nicht "keine offenen Flüge" vortäuschen); Netzwerk/5xx meldet der Interceptor (Statusleiste)
    api.get('/dji/fluege-ohne-pilot').then(r => setFluege(r.data as OffenerFlug[])).catch(() => {})
  }, [])
  useConnectionRetry(laden)

  useEffect(() => {
    laden()
    const t = setInterval(laden, 15000)
    return () => clearInterval(t)
  }, [laden])

  if (!fluege.length) return null
  return (
    <div className="mb-3 space-y-2">
      <AufforderungsGruppe id="ohne-pilot" anzahl={fluege.length} testid="gruppe-ohne-pilot"
        titel={`${fluege.length} Flüge ohne Pilot`} farbe="border-amber-500/40 bg-amber-500/10 text-amber-100"
        icon={<UserCheck className="h-5 w-5 shrink-0 text-amber-300" />}>
        {fluege.map(f => (
          <FlugMeldung key={f.id} flug={f} istPilot={funktionen.includes('PILOT')} istCoPilot={funktionen.includes('CO_PILOT')} onErledigt={laden} />
        ))}
      </AufforderungsGruppe>
    </div>
  )
}
